/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Component for displaying a bike's complete maintenance history.
Includes timeline view of repairs, issues, and maintenance activities.
*/

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, format, isValid } from "date-fns";
import { Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MaintenanceHistoryItem } from "@/components/maintenance/MaintenanceHistoryItem";
import { Skeleton } from "@/components/ui/skeleton";
import { UpdateMaintenanceDialog } from "@/components/maintenance/UpdateMaintenanceDialog";
import { UpdateMaintenanceHistoryDialog } from "@/components/maintenance/UpdateMaintenanceHistoryDialog";

interface MaintenanceBikeHistoryProps {
  bikeId: string;
}

export function MaintenanceBikeHistory({ bikeId }: MaintenanceBikeHistoryProps) {
  // Fetch all sites for mapping site IDs to names
  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const { data, error } = await supabase.from("sites").select("id, name");
      if (error) throw error;
      return data || [];
    },
  });

  // Helper to get site name by ID
  const getSiteName = (siteId: string | null | undefined) => {
    if (!siteId || !sites) return "N/A";
    const site = sites.find((s: any) => s.id === siteId);
    return site ? site.name : siteId;
  };

  // Fetch maintenance issues with booking and user/route/ride_type info
  const {
    data: maintenanceIssues,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["bike-maintenance", bikeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("maintenance_issues")
        .select(`
          *,
          reported_by (id, username),
          booking:booking_id (
            id,
            user_id,
            ride_type,
            start_location_id,
            end_location_id,
            users (id, username)
          ),
          maintenance_issues_history (
            id,
            bike_id,
            issue_id,
            status,
            notes,
            cost,
            changed_by (username),
            change_timestamp
          )
        `)
        .eq("bike_id", bikeId)
        .order("reported_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!bikeId,
  });

  const getBadgeColor = (status: string) => {
    switch (status) {
      case "ok": return "bg-green-100 text-green-800";
      case "reported": return "bg-yellow-100 text-yellow-800";
      case "in_progress": return "bg-blue-100 text-blue-800";
      case "resolved": return "bg-green-100 text-green-800";
      case "broken": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown date";
    
    const date = new Date(dateString);
    return isValid(date) && date.getFullYear() > 1970
      ? format(date, 'PPP')
      : "Unknown date";
  };
  
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return "Unknown date";
    
    const date = new Date(dateString);
    return isValid(date) && date.getFullYear() > 1970
      ? format(date, 'PPP p')
      : "Unknown date";
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (!maintenanceIssues || maintenanceIssues.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-gray-100 p-3">
          <Wrench className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="mt-4 text-lg font-medium">No Maintenance Records</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          This eBike has no maintenance history yet.
        </p>
      </div>
    );
  }

  // Defensive sort: ensure maintenanceIssues are sorted by reported_at descending
  const sortedMaintenanceIssues = [...maintenanceIssues].sort((a, b) => {
    const dateA = new Date(a.reported_at).getTime();
    const dateB = new Date(b.reported_at).getTime();
    return dateB - dateA;
  });

  return (
    <div className="space-y-6">
      <div className="space-y-6">
        {sortedMaintenanceIssues.map((issue: any) => (
          <div key={issue.id} className="rounded-lg border shadow-sm">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">
                      {issue.issue_type.replace("_", " ").charAt(0).toUpperCase() + 
                      issue.issue_type.slice(1)}
                    </h3>
                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getBadgeColor(issue.status)}`}>
                      {issue.status}
                    </span>
                  </div>
                  {/* User, Route, Ride Type, Cost */}
                  <div className="mt-1 text-sm text-muted-foreground flex flex-col gap-1">
                    {/* User */}
                    {issue.booking?.users?.username && (
                      <span>
                        <span className="font-semibold">User:</span> {issue.booking.users.username}
                      </span>
                    )}
                    {/* Route */}
                    {(issue.booking?.start_location_id || issue.booking?.end_location_id) && (
                      <span>
                        <span className="font-semibold">Route:</span> {getSiteName(issue.booking?.start_location_id)} <span className="mx-1">→</span> {getSiteName(issue.booking?.end_location_id)}
                      </span>
                    )}
                    {/* Ride Type */}
                    {issue.booking?.ride_type && (
                      <span>
                        <span className="font-semibold">Ride Type:</span> <span className="ml-1 px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-semibold uppercase tracking-wide text-xs">{issue.booking.ride_type}</span>
                      </span>
                    )}
                    {/* Cost */}
                    {issue.cost !== null && issue.cost !== undefined && (
                      <span>
                        <span className="font-semibold">Cost:</span> <span className="text-green-700 font-medium">£{issue.cost}</span>
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Reported by {issue.reported_by?.username} on {formatDate(issue.reported_at)}
                  </p>
                </div>
              </div>

              {issue.notes && (
                <div className="mt-3">
                  <p className="text-sm">{issue.notes}</p>
                </div>
              )}

              <div className="mt-4 flex justify-end">
                <UpdateMaintenanceDialog issue={issue} onUpdate={refetch} />
              </div>
              {issue.maintenance_issues_history && issue.maintenance_issues_history.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <HistoryTimeline 
                    history={[...issue.maintenance_issues_history].sort((a, b) => {
                      const dateA = new Date(a.change_timestamp).getTime();
                      const dateB = new Date(b.change_timestamp).getTime();
                      return dateB - dateA;
                    })}
                    getBadgeColor={getBadgeColor}
                    formatDateTime={formatDateTime}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryTimeline({ history, getBadgeColor, formatDateTime }: { history: any[], getBadgeColor: (status: string) => string, formatDateTime: (dateString: string | null) => string }) {
  const [open, setOpen] = React.useState(false);
  if (!history || history.length === 0) return null;
  return (
    <div>
      <button
        className={`flex items-center gap-2 px-3 py-2 rounded-md border border-blue-200 bg-blue-50 text-blue-700 font-medium transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-300 mb-2`}
        style={{ minWidth: 180 }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`transition-transform ${open ? 'rotate-180' : ''}`}
          style={{ display: 'flex', alignItems: 'center' }}>
          <ChevronDown size={18} />
        </span>
        {open ? "Hide Full History" : `Show Full History (${history.length})`}
      </button>
      {open && (
        <div className="space-y-3 border border-blue-100 bg-blue-50 rounded-md p-3">
          {history.map((history, index) => (
            <div key={history.id} className="rounded-md bg-white p-3 text-sm shadow-sm border border-gray-100">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${getBadgeColor(history.status)}`}>{history.status}</span>
                <span className="text-muted-foreground">by {history.changed_by?.username}</span>
                {history.cost !== null && (
                  <span className="ml-auto text-sm">Cost: £{history.cost}</span>
                )}
              </div>
              {history.notes && (
                <p className="mt-1 text-muted-foreground">{history.notes}</p>
              )}
              <time className="mt-1 block text-xs text-muted-foreground">{formatDateTime(history.change_timestamp)}</time>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
